import React, { useCallback } from 'react';
import { openWebAppLink } from '@landr/core';
import { Button } from '@landr/maestro';
import { Trans } from '@lingui/macro';
import { usePurchaseControllerContext } from 'contexts/PurchaseControllerContext/PurchaseControllerContext';
import { useExecutionEnvironmentContext } from 'contexts/ExecutionEnvironmentContext/ExecutionEnvironmentContext';
import { useSubscriptionsAndAllowedFeaturesContext } from 'contexts/SubscriptionsAndAllowedFeaturesContext/SubscriptionsAndAllowedFeaturesContext';
import { useFreeUserFreeDownloadUpsellModalContext } from 'modals/UpsellModals/FreeUserFreeDownloadUpsellModalContext';
import { DownloadableItem } from 'types';
import { analytics } from 'utils/analytics';
import {
    CtaGoal,
    CtaType,
    InteractionSource,
} from 'utils/analytics/AnalyticsV2Service.generated';
import { storeAttemptedDownloadAction } from 'hooks/useStoreAttemptedDownloadAction';
import { checkIfAbsoluteUrl, CONFIG } from 'utils';
import { useUpsellActionsContext } from '../../context/UpsellActionsContext';
import { FREE_DOWNLOAD_CODE } from '../BasicDownload/useBasicDownload';

export const useBuyPlanButton = ({
    downloadableItem,
    interactionSource,
    purchaseLink,
    selectedPlan,
}: {
    downloadableItem: DownloadableItem | null;
    interactionSource: InteractionSource;
    purchaseLink: string;
    selectedPlan: string;
}) => {
    const { closeModal } = useFreeUserFreeDownloadUpsellModalContext();
    const {
        startPurchase,
        isLoading: isPurchaseLoading,
        queuedItemIDs,
    } = usePurchaseControllerContext();
    const { isDAWPlugin } = useExecutionEnvironmentContext();

    const isLoading =
        isPurchaseLoading && queuedItemIDs.includes(downloadableItem?.id || '');

    const isDisabled = !purchaseLink || isLoading;

    const handleOnClick = useCallback(() => {
        const isAttemptingToDownloadForFree =
            selectedPlan === FREE_DOWNLOAD_CODE;

        const isAbsoluteUrl = checkIfAbsoluteUrl(purchaseLink);
        if (!isAttemptingToDownloadForFree) {
            const destinationUrl = isAbsoluteUrl
                ? new URL(purchaseLink)
                : new URL(purchaseLink, window.location.origin);

            destinationUrl.searchParams.delete('returnUrl');

            analytics.trackCTAClicked({
                ctaType: CtaType.Button,
                ctaGoal: CtaGoal.Checkout,
                interactionSource,
                ctaText: 'Continue to checkout',
                ctaDestination: destinationUrl.toString(),
            });
        }

        if (isAttemptingToDownloadForFree) {
            downloadableItem &&
                startPurchase({
                    item: downloadableItem,
                    interactionSource:
                        InteractionSource.UpsellModalBasicDownload,
                });

            closeModal();
        } else {
            downloadableItem && storeAttemptedDownloadAction(downloadableItem);

            if (isDAWPlugin) {
                window.open(purchaseLink, '_blank');
            } else {
                openWebAppLink(CONFIG.VITE_APP_ENV, purchaseLink);
            }
        }
    }, [
        downloadableItem,
        interactionSource,
        purchaseLink,
        closeModal,
        startPurchase,
        isDAWPlugin,
        selectedPlan,
    ]);

    return { isDisabled, handleOnClick };
};

export const _BuyPlanButton = ({
    isDisabled,
    handleOnClick,
}: {
    isDisabled: boolean;
    handleOnClick: () => void;
}) => (
    <Button
        isDisabled={isDisabled}
        variant="secondary"
        onClick={handleOnClick}
        isRounded={true}
    >
        <Trans>Continue</Trans>
    </Button>
);

const BuyPlanButton: React.FC = () => {
    const { downloadableItem } = useFreeUserFreeDownloadUpsellModalContext();
    const { samplePaidContentAccess } =
        useSubscriptionsAndAllowedFeaturesContext();

    const { purchaseLink, selectedPlan } = useUpsellActionsContext();

    const { isDisabled, handleOnClick } = useBuyPlanButton({
        downloadableItem,
        selectedPlan,
        purchaseLink,
        interactionSource: samplePaidContentAccess?.isAllowed
            ? InteractionSource.UpsellModalPaidUserPaidDownload
            : InteractionSource.UpsellModalFreeUserPaidDownload,
    });

    return (
        <_BuyPlanButton isDisabled={isDisabled} handleOnClick={handleOnClick} />
    );
};

export default BuyPlanButton;
